// src/App.tsx
import React from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { ToastProvider } from '@/components/common/UI/Toast'
import { QueryProvider } from '@/providers/QueryProvider'
import { useAuthStore } from '@/store'
import { MainLayout } from '@/components/common/Layout/MainLayout'

// Pages
import Home from '@/pages/Home'
import Login from '@/pages/Auth/Login'
import Register from '@/pages/Auth/Register'
import VerifyEmail from '@/pages/Auth/VerifyEmail'
import OAuthCallback from '@/pages/Auth/OAuthCallback'
import Dashboard from '@/pages/Dashboard'
import Vacancies from '@/pages/Vacancies'
import VacancyDetail from '@/pages/Vacancies/[id]'
import VacancyCreate from '@/pages/Vacancies/Create'
import Resumes from '@/pages/Resumes'
import ResumeDetail from '@/pages/Resumes/[id]'
import Companies from '@/pages/Companies'
import CompanyDetail from '@/pages/Companies/[id]'
import CompanyCreate from '@/pages/Companies/Create'
import Profile from '@/pages/Profile'
import Notifications from '@/pages/Notifications'
import Applications from '@/pages/Applications'
import SalaryCalculator from '@/pages/SalaryCalculator'
import Help from '@/pages/Help'
import FAQ from '@/pages/FAQ'
import Admin from '@/pages/Admin'
import News from '@/pages/News'
import NewsArticle from '@/pages/News/Article'
import Articles from '@/pages/News/Articles'
import LegalPage from '@/pages/Legal'
import ServicesPage from '@/pages/Services'
import SkillVerification from '@/pages/Services/SkillVerification'
import Subscriptions from '@/pages/Subscriptions'
import { RouteSeoHead } from '@/components/common/SeoHead'

const RouteSEO = () => {
  const { pathname, search } = useLocation()
  return <RouteSeoHead pathname={pathname} search={search} />
}

// Route Guards
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

const EmployerRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthStore()

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (user?.role !== 'employer' && user?.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  if (user?.role !== 'admin') return <Navigate to="/" replace />
  return <>{children}</>
}

function App() {
  return (
    <ToastProvider position="top-right" maxToasts={5}>
      <QueryProvider>
        <BrowserRouter>
        <RouteSEO />
        <MainLayout>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<NewsArticle />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<NewsArticle />} />
            <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />

            {/* Protected routes */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />

            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/applications" element={<ProtectedRoute><Applications /></ProtectedRoute>} />

            <Route path="/vacancies" element={<Vacancies />} />
            <Route path="/salary-calculator" element={<SalaryCalculator />} />
            <Route path="/help" element={<Help />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/cookies" element={<LegalPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/services/skill-verification" element={<SkillVerification />} />
            <Route path="/subscriptions" element={<Subscriptions />} />
            <Route path="/vacancies/create" element={<EmployerRoute><VacancyCreate /></EmployerRoute>} />
            <Route
              path="/vacancies/:id"
              element={<VacancyDetail />}
            />

            <Route
              path="/resumes"
              element={
                <ProtectedRoute>
                  <Resumes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/resumes/:id"
              element={
                <ProtectedRoute>
                  <ResumeDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/companies"
              element={
                <ProtectedRoute>
                  <Companies />
                </ProtectedRoute>
              }
            />
            <Route path="/companies/create" element={<EmployerRoute><CompanyCreate /></EmployerRoute>} />
            <Route
              path="/companies/:id"
              element={
                <ProtectedRoute>
                  <CompanyDetail />
                </ProtectedRoute>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MainLayout>

        <Toaster
          position="top-right"
          toastOptions={{
            duration: 5000,
            className: 'text-sm',
            success: {
              iconTheme: {
                primary: '#22c55e',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        </BrowserRouter>
      </QueryProvider>
    </ToastProvider>
  )
}

export default App
